// src/components/AppIcon.jsx
import React from "react";
import { Box, Image } from "@mantine/core";
import { resolveIcon } from "../utils/iconResolver";

function toNumeric(value) {
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : undefined;
  }

  if (typeof value === "string") {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : undefined;
  }

  return undefined;
}

export default function AppIcon({
  styles,
  slot,
  boxSize = 100,
  radius = 0,
  fallback = null,
  alt,
}) {
  const icon = resolveIcon(styles, { slot });
  const slotConfig = styles?.assets?.icons?.[slot];

  const resolvedWidth = toNumeric(icon?.width) ?? toNumeric(slotConfig?.width);
  const resolvedHeight = toNumeric(icon?.height) ?? toNumeric(slotConfig?.height);

  const frameWidth = resolvedWidth ?? resolvedHeight ?? boxSize;
  const frameHeight = resolvedHeight ?? resolvedWidth ?? boxSize;

  const frameProps = {
    w: frameWidth,
    h: frameHeight,
    style: { display: "grid", placeItems: "center", overflow: "hidden" },
  };

  const hasExplicitDimensions = resolvedWidth !== undefined || resolvedHeight !== undefined;

  if (!icon) {
    if (!fallback) {
      return <Box {...frameProps} />;
    }

    if (React.isValidElement(fallback)) {
      const fallbackStyle = {
        ...fallback.props?.style,
      };

      if (hasExplicitDimensions) {
        if (fallbackStyle.width === undefined) fallbackStyle.width = "100%";
        if (fallbackStyle.height === undefined) fallbackStyle.height = "100%";
        if (fallbackStyle.maxWidth === undefined) fallbackStyle.maxWidth = "100%";
        if (fallbackStyle.maxHeight === undefined) fallbackStyle.maxHeight = "100%";
      }

      const fallbackProps = { style: fallbackStyle };

      if (
        typeof fallback.type !== "string" &&
        typeof frameWidth === "number" &&
        typeof frameHeight === "number" &&
        hasExplicitDimensions
      ) {
        fallbackProps.size = Math.min(frameWidth, frameHeight);
      }

      return <Box {...frameProps}>{React.cloneElement(fallback, fallbackProps)}</Box>;
    }

    return <Box {...frameProps}>{fallback}</Box>;
  }

  const explicitW = resolvedWidth;
  const explicitH = resolvedHeight;

  return (
    <Box {...frameProps}>
      <Image
        src={icon.src}
        alt={alt || icon.alt || "Brand"}
        width={explicitW}
        height={explicitH}
        fit="contain"
        radius={radius}
        imageProps={{
          srcSet: icon.srcSet, // Mantine 6: must be in imageProps
          sizes:
            typeof frameWidth === "number" ? `${frameWidth}px` : undefined, // hint to the browser
          loading: "lazy",
          decoding: "async",
        }}
        styles={{
          image: { maxWidth: "100%", maxHeight: "100%", display: "block" },
          root: { width: "100%", height: "100%" },
        }}
      />
    </Box>
  );
}